import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  Globe,
  KeyRound,
  List,
  Loader2,
  Mail,
  Plug,
  RefreshCw,
  Shield,
  SlidersHorizontal,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_VALIDATION_POLICY, ValidationPolicy } from '../../../Shared/Utils/validationPolicy';
import { getSystemConfiguration } from '../../Services/AdminService';

/**
 * Business Support → System Configuration hub. Two cards branch into the Email Config and
 * Password Config detail screens; each card shows the live on/off state of its flag so the
 * admin sees the current configuration before opening it.
 */
function SystemConfigurationComponent() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<ValidationPolicy>(DEFAULT_VALIDATION_POLICY);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSystemConfiguration();
      if (response.success && response.data) {
        setConfig({ ...DEFAULT_VALIDATION_POLICY, ...response.data });
      }
    } catch {
      // Keep the secure defaults on screen when the load fails.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const cards = [
    {
      key: 'email-config',
      label: 'Email Config',
      description: 'Format validation rules for portal emails',
      icon: <Mail size={18} />,
      accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      path: '/admin/settings/email-config',
      enabled: config.emailFormatValidationEnabled,
    },
    {
      key: 'password-config',
      label: 'Password Config',
      description: 'Password strength rules across the portal',
      icon: <KeyRound size={18} />,
      accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
      path: '/admin/settings/password-config',
      enabled: config.passwordPolicyValidationEnabled,
    },
    {
      key: 'security',
      label: 'Security',
      description: 'Tokens, lockout, cookies and seeding policy',
      icon: <Shield size={18} />,
      accent: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
      path: '/admin/settings/system-config/security',
      enabled: true,
    },
    {
      key: 'integrations',
      label: 'Integrations',
      description: 'OpenSearch, Loki and third-party endpoints',
      icon: <Plug size={18} />,
      accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
      path: '/admin/settings/system-config/integrations',
      enabled: true,
    },
    {
      key: 'email',
      label: 'Email Provider',
      description: 'Provider selection and sender credentials',
      icon: <Mail size={18} />,
      accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      path: '/admin/settings/system-config/email',
      enabled: true,
    },
    {
      key: 'observability',
      label: 'Observability',
      description: 'Request logging and actuator exposure',
      icon: <Activity size={18} />,
      accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
      path: '/admin/settings/system-config/observability',
      enabled: true,
    },
    {
      key: 'upload',
      label: 'File Upload',
      description: 'Multipart size limits',
      icon: <Upload size={18} />,
      accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
      path: '/admin/settings/system-config/upload',
      enabled: true,
    },
    {
      key: 'pagination',
      label: 'Pagination',
      description: 'Default and maximum page sizes',
      icon: <List size={18} />,
      accent: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400',
      path: '/admin/settings/system-config/pagination',
      enabled: true,
    },
    {
      key: 'cors',
      label: 'CORS',
      description: 'Cross-origin request policy',
      icon: <Globe size={18} />,
      accent: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
      path: '/admin/settings/system-config/cors',
      enabled: true,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <SlidersHorizontal size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">System Configuration</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Choose a section to configure
            </p>
          </div>
        </div>
        <button
          onClick={fetchConfig}
          title="Refresh"
          disabled={loading}
          className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60 sm:ml-auto"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
        {cards.map((card, idx) => (
          <motion.button
            key={card.key}
            type="button"
            onClick={() => navigate(card.path)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 + idx * 0.05 }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-cyan-500/40 hover:bg-slate-900"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${card.accent}`}>
                {card.icon}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-100">{card.label}</p>
                <p className="text-[11px] font-semibold text-slate-500">{card.description}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                  card.enabled
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                    : 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                }`}
              >
                {loading ? '···' : card.enabled ? 'On' : 'Off'}
              </span>
              <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-cyan-400" />
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export default SystemConfigurationComponent;
